import React, {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import useBmoStore from 'common/stores/bmoStore';
import CaseContent from '../components/cases/CaseContent';
import useUserStore from 'common/stores/user';
import {sliceFullName} from 'utils/sliceFullName';
import {Button, Layout, Text} from '@phonectas/unifon-ui-kit';
import CancelDialog from '../components/cases/CancelDialog';
import {useSmallScreen} from 'common/hooks/useScreenSize';
import dayjs from 'dayjs';

export interface CaseItem {
    id: number;
    createdAt: string;
    createdAtTelenor: string;
    createdBy: string;
    description: string;
    name: string;
    reporter: string;
    responsible: string;
    severity: string;
    status: string;
    statusId: number;
    unifon_subject: string;
    zendeskRef: string | null;
}

interface CaseProps {
    data?: CaseItem[];
}

const Case: React.FC<CaseProps> = ({data}) => {
	const [t] = useTranslation();
	const addNote = useBmoStore((state) => state.addNote);
	const getTicketId = useBmoStore((state) => state.getTicketId);
	const ticketById = useBmoStore((state) => state.ticketById);
	const isSmallScreen = useSmallScreen();
	const {user} = useUserStore();
	const location = useLocation();
	const {search} = location;
	const firstName: string = user ? sliceFullName(user.name, true) ?? '' : '';
	const lastName: string = user ? sliceFullName(user.name, false) ?? '' : '';
	const [noteMsg, setNoteMsg] = useState('');
	const [showCancelDialog, setShowCancelDialog] = useState(false);
	const onShowCancelDialog = useCallback(() => setShowCancelDialog(true), []);
	const onHideCancelDialog = useCallback(() => {
		setShowCancelDialog(false);
	}, []);
	const handleCancel = useCallback(() => {
		setShowCancelDialog(false);
	}, []);
	const handleNoteChange = useCallback(({target: {value}}: React.ChangeEvent<HTMLTextAreaElement>) => {
		setNoteMsg(value);
	}, [setNoteMsg]);
	const id = search ? search.match(/\d+/)?.[0] ?? null : null;
	const activeCase = id === ticketById?.id;
	const ticketList = data?.find((obj) => obj.id.toString() === id);
	// Typed text belongs to one case; drop it when another case is opened.
	useEffect(() => {
		setNoteMsg('');
	}, [id]);
	const fetchCase = useCallback(async () => {
		if (!activeCase) {
			try {
				if(id) {
					await getTicketId(id);
				}
			} catch (err) {
				console.error(err);
			}
		}
	}, [id, getTicketId, activeCase]);

	useEffect(() => {
		fetchCase();
	}, [fetchCase]);

	const handleAddNote = useCallback(async () => {
		try {
			if (id && noteMsg.trim()) {
				await addNote(id, {
					authorFirstName: firstName,
					authorLastName: lastName,
					date: dayjs().toISOString(),
					text: noteMsg,
					zendesk_ref: ticketList?.zendeskRef ?? '',
				}, () => {
					getTicketId(id);
				});
				setNoteMsg('');
			}
		} catch (err) {
			console.error(err);
		}
	}, [addNote, id, noteMsg, firstName, lastName, ticketList?.zendeskRef, getTicketId]);

	return (
		<Layout
			style={{width: isSmallScreen ? '780px' : 'fit-content'}}
		>
			<Layout fullWidth style={{justifyContent: 'space-between'}}>
				<Text variant="h3">
					{
						!id ? null : `${t('Case')}: ${id}`
					}
				</Text>
				<Text variant="h3">
					{
						!ticketList?.zendeskRef ? null : `Zendesk: ${ticketList?.zendeskRef}`
					}
				</Text>
			</Layout>
			{
				activeCase && (
					<>
						<CaseContent
							key={id}
							currentTicket={ticketById}
							ticketList={ticketList ?? null}
							noteMsg={noteMsg}
							handleNoteChange={handleNoteChange}
							handleAddNote={handleAddNote}
						/>
						<Layout justifyContent="end" fullWidth>
							<Button disabled onClick={onShowCancelDialog}>{t('Cancel ticket')}</Button>
						</Layout>
						<CancelDialog
							open={showCancelDialog}
							close={onHideCancelDialog}
							id={id}
							handleChange={handleCancel}
						/>
					</>
				)
			}
		</Layout>
	);
}

export default Case;
